/* eslint-disable */
import React, { useMemo } from "react";
import { Layer, Shape, Stage } from "react-konva";

// Look, fixed (owner decision 2026-10-12): style A, nothing here is a setting.
const TINT = 0.55; // colour strength of a cell
const DELTA_C = 3; // cells this far from room (or more) are shown
const NUM_PX = 8; // number size
const COLS = 32;
const ROWS = 24;
// fixed heatmap: [degrees C, r, g, b]; room (~27 C) is soft green, colours blend in between
const HEAT = [[-10, 0, 0, 110], [0, 0, 70, 255], [15, 0, 190, 255], [27, 140, 220, 140],
  [45, 255, 230, 0], [65, 255, 140, 0], [85, 230, 30, 20], [110, 140, 0, 0]];

const clamp = (x) => Math.max(0, Math.min(1, x));
const colour = (T) => {
  let i = 0;
  while (i < HEAT.length - 2 && T > HEAT[i + 1][0]) i++;
  const [t0, ...a] = HEAT[i], [t1, ...b] = HEAT[i + 1], f = clamp((T - t0) / (t1 - t0));
  return a.map((x, k) => Math.round(x + (b[k] - x) * f));
};

// Which cells to show: all of them (all=true); else, if boxes ([x0,y0,x1,y1] in 0-1 picture fractions) exist, every cell whose middle is inside a box;
// else only those DELTA_C or more away from room (hot or cold).
// For viewing only - temperature checks still use the circle readings.
export const gridCells = ({ t, room, p }, all = false, boxes = []) => { try {
  const mid = (c, r) => { const u = (c + 0.5) / COLS, v = (r + 0.5) / ROWS; return [0, 1].map((k) => (1 - v) * ((1 - u) * p[k] + u * p[2 + k]) + v * ((1 - u) * p[6 + k] + u * p[4 + k])); };
  const inBox = (c, r) => { const [x, y] = mid(c, r); return boxes.some(([x0, y0, x1, y1]) => x >= x0 && x <= x1 && y >= y0 && y <= y1); };
  return t.map((x, i) => { const T = x == null ? room : x, c = i % COLS, r = Math.floor(i / COLS); return { c, r, T, a: all || (boxes.length ? inBox(c, r) : Math.abs(T - room) >= DELTA_C) ? 1 : 0 }; })
    .filter((x) => x.a && Number.isFinite(x.T)); // odd data (strings, NaN, wrong shape) shows nothing, never throws and unmounts the page
  } catch (e) { return []; } };

const GridHUD = ({ grid, rect, opacity = 1, isFahrenheit, all = false }) => {
  const ok = grid?.t?.length === COLS * ROWS && grid?.p?.length === 8;
  const boxes = useMemo(() => (Array.isArray(grid?.boxes) ? grid.boxes : []).map((x) => x?.b).filter(Array.isArray), [grid]);
  const cells = useMemo(() => (ok ? gridCells(grid, all, boxes) : []), [grid, ok, all, boxes]);
  if (!ok || !cells.length) return null;
  const W = rect?.width ?? 0, H = rect?.height ?? 0, p = grid.p;
  // point of the grid at (u, v) in 0-1, blended from the 4 outer corners
  const at = (u, v) => [W, H].map((size, k) =>
    size * ((1 - v) * ((1 - u) * p[k] + u * p[2 + k]) + v * ((1 - u) * p[6 + k] + u * p[4 + k])));
  const draw = (konva) => { try {
    const g = konva._context;
    g.textAlign = "center"; g.textBaseline = "middle"; g.font = `600 ${NUM_PX}px system-ui, Arial`;
    cells.forEach(({ c, r, T, a }) => {
      const col = colour(T);
      g.beginPath();
      [[c, r], [c + 1, r], [c + 1, r + 1], [c, r + 1]].forEach(([x, y], k) => g[k ? "lineTo" : "moveTo"](...at(x / COLS, y / ROWS)));
      g.closePath();
      g.fillStyle = `rgba(${col},${TINT * a})`; g.fill();
      g.strokeStyle = `rgba(255,255,255,${0.45 * a})`; g.lineWidth = 0.8; g.stroke();
      if (a <= 0.15) return;
      const lum = (0.299 * col[0] + 0.587 * col[1] + 0.114 * col[2]) / 255;
      const light = lum * TINT + 0.45 * (1 - TINT) > 0.55;
      g.fillStyle = light ? `rgba(20,20,25,${a})` : `rgba(255,255,255,${a})`;
      g.shadowColor = light ? "transparent" : `rgba(0,0,0,${0.8 * a})`; g.shadowBlur = light ? 0 : 2;
      g.fillText(Math.round(isFahrenheit ? T * 1.8 + 32 : T), ...at((c + 0.5) / COLS, (r + 0.5) / ROWS));
      g.shadowBlur = 0;
    });
  } catch (e) {} };
  return (
    <Stage width={W} height={H} style={{ maxWidth: "100%", position: "absolute", inset: 0, pointerEvents: "none" }}>
      <Layer opacity={opacity}>
        <Shape sceneFunc={draw} listening={false} />
      </Layer>
    </Stage>
  );
};

export default GridHUD;
